import _ from 'lodash';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {compareName} from '../../../../../srcCommon/helpers/localeHelpers';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {
  getContactSourceFromMessageType,
  getMessageTypeFromChannelType,
} from '../../../../helpers/modelHelpers';
import {selectCardViaPriorityOrder} from '../../../../helpers/models/cardHelpers';
import {splitConversationsIntoRecentAndOlder} from '../../../../helpers/models/conversationHelpers';
import {globalNamespace} from '../../../../helpers/models/namespaceHelpers';
import {ActionWithPayload, batch} from '../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {FrameTypesEnum} from '../../../../models/api/apiTypes';
import {CardModel} from '../../../../models/api/cardModel';
import {ChannelTypesEnum} from '../../../../models/api/channelCommonTypes';
import {
  buildContactForHandle,
  ContactModel,
  ContactSourcesEnum,
  parseContactUid,
} from '../../../../models/api/contactModel';
import {ContactDetailsTabsEnum} from '../../../../models/api/localPreferencesModel';
import {RecipientRolesEnum} from '../../../../models/api/recipientModel';
import {VisualContactModel} from '../../../../models/api/visualContactModel';
import {HttpError} from '../../../../services/http/httpError';
import {findPanelSequence} from '../../../../services/panels';
import {buildPanelCancelToken, findPanelCancelToken} from '../../../../services/panelsCancellers';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {fetchOrGetAccountAsyncAction} from '../../../data/accounts/accountsActions';
import {
  createCompanyCardAsyncAction,
  createPrivateCardAsyncAction,
  fetchCardConversationsAsyncAction,
  fetchContactConversationsAsyncAction,
  searchForCardsAsyncAction,
} from '../../../data/cards/cardsActions';
import {updateDataAction} from '../../../data/dataGroupsActions';
import {getCards} from '../../../data/dataSelectors';
import {
  updateContactDetailsPreferredTabAction,
  updateSelectedSideFrameIdAction,
} from '../../../data/localPreferences/localPreferencesActions';
import {composeMessageAction} from '../../../data/messages/draftMessagesActions';
import {getIsUserVisitor} from '../../../data/teammates/teammatesSelectors';
import {
  createOrUpdateCalendarEventDraftAction,
  navigateCalendarsAction,
} from '../../calendars/calendarsPanelActions';
import {PanelActionPayload, setPanelAction} from '../../panelsActions';
import {getFlatPanels, getPanels, makeGetAppPanelIdFromPanel} from '../../panelsSelectors';
import {panelIsContactDetailsPanel} from '../../panelsState';
import {
  AccountDetailsPanelId,
  CalendarsPanelId,
  ContactCardPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  makeCalendarsPanelId,
  MessagePanelId,
  NewPanelId,
  PanelTypesEnum,
  RCalendarsPanelId,
  ViewerPanelId,
} from '../../panelStateBase';
import {
  contactDetailsOpenAccountDetailsAction,
  contactDetailsScrollToSectionAction,
  popoutContactDetailsAction,
  updateContactDetailsPanelSelectedContactAction,
} from '../contactDetailsPanelActions';
import {
  ContactDetailsPanelLocationsEnum,
  ContactDetailsScrollSectionsEnum,
} from '../contactDetailsPanelState';
import {makeGetContactCardPanel} from './contactCardPanelSelectors';
import {ContactCardLocationsEnum} from './contactCardPanelState';

/*
 * Type enum.
 */

export enum ContactCardPanelActionsEnum {
  FETCH_CONTACT_CARD_DATA = '@@panels/contact-card/FETCH_CONTACT_CARD_DATA',
  FETCH_CONTACT_CARD_DATA_COMPLETED = '@@panels/contact-card/FETCH_CONTACT_CARD_DATA_COMPLETED',
  FETCH_CONTACT_CARD_ACCOUNT = '@@panels/contact-card/FETCH_CONTACT_CARD_ACCOUNT',
  FETCH_CONTACT_CARD_ACCOUNT_COMPLETED = '@@panels/contact-card/FETCH_CONTACT_CARD_ACCOUNT_COMPLETED',

  FETCH_CONTACT_CARD_CONVERSATIONS = '@@panels/contact-card/FETCH_CONTACT_CARD_CONVERSATIONS',
  FETCH_CONTACT_CARD_CONVERSATIONS_COMPLETED = '@@panels/contact-card/FETCH_CONTACT_CARD_CONVERSATIONS_COMPLETED',
  FETCH_CONTACT_CARD_CONVERSATIONS_FAILED = '@@panels/contact-card/FETCH_CONTACT_CARD_CONVERSATIONS_FAILED',

  SELECTED_CARD_UPDATED = '@@panels/contact-card/SELECTED_CARD_UPDATED',
}

/*
 * Action Payloads.
 */

type ContactCardPanelActionPayload = PanelActionPayload<ContactCardPanelId>;

interface FetchContactCardDataAction extends ActionWithPayload<ContactCardPanelActionPayload> {
  type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_DATA;
}

interface FetchContactCardDataCompletedActionPayload extends ContactCardPanelActionPayload {
  selectedCardId?: number;
}
interface FetchContactCardDataCompletedAction
  extends ActionWithPayload<FetchContactCardDataCompletedActionPayload> {
  type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_DATA_COMPLETED;
}

interface FetchContactCardAccountAction extends ActionWithPayload<ContactCardPanelActionPayload> {
  type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_ACCOUNT;
}

interface FetchContactCardAccountCompletedAction extends ActionWithPayload<ContactCardPanelActionPayload> {
  type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_ACCOUNT_COMPLETED;
}

interface SelectedCardUpdatedActionPayload extends ContactCardPanelActionPayload {
  selectedCardId?: number;
}

interface SelectedCardUpdatedAction extends ActionWithPayload<SelectedCardUpdatedActionPayload> {
  type: ContactCardPanelActionsEnum.SELECTED_CARD_UPDATED;
}

interface FetchContactCardConversationsAction extends ActionWithPayload<ContactCardPanelActionPayload> {
  type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_CONVERSATIONS;
}

interface FetchContactCardConversationsCompletedActionPayload extends ContactCardPanelActionPayload {
  conversationIds: ReadonlyArray<number>;
}

interface FetchContactCardConversationsCompletedAction
  extends ActionWithPayload<FetchContactCardConversationsCompletedActionPayload> {
  type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_CONVERSATIONS_COMPLETED;
}

interface FetchContactCardConversationsFailedAction extends ActionWithPayload<ContactCardPanelActionPayload> {
  type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_CONVERSATIONS_FAILED;
}

/*
 * Action Creators.
 */

function fetchContactCardDataAction(panelId: ContactCardPanelId): FetchContactCardDataAction {
  return {
    type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_DATA,
    payload: {
      panelId,
    },
  };
}

function fetchContactCardDataCompletedAction(
  panelId: ContactCardPanelId,
  selectedCardId?: number,
): FetchContactCardDataCompletedAction {
  return {
    type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_DATA_COMPLETED,
    payload: {
      panelId,
      selectedCardId,
    },
  };
}

function fetchContactCardAccountAction(panelId: ContactCardPanelId): FetchContactCardAccountAction {
  return {
    type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_ACCOUNT,
    payload: {
      panelId,
    },
  };
}

function fetchContactCardAccountCompletedAction(
  panelId: ContactCardPanelId,
): FetchContactCardAccountCompletedAction {
  return {
    type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_ACCOUNT_COMPLETED,
    payload: {
      panelId,
    },
  };
}

function selectedCardUpdatedAction(
  panelId: ContactCardPanelId,
  selectedCardId: number,
): SelectedCardUpdatedAction {
  return {
    type: ContactCardPanelActionsEnum.SELECTED_CARD_UPDATED,
    payload: {
      panelId,
      selectedCardId,
    },
  };
}

function fetchContactCardConversationsAction(
  panelId: ContactCardPanelId,
): FetchContactCardConversationsAction {
  return {
    type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_CONVERSATIONS,
    payload: {
      panelId,
    },
  };
}

function fetchContactCardConversationsCompletedAction(
  panelId: ContactCardPanelId,
  conversationIds: ReadonlyArray<number>,
): FetchContactCardConversationsCompletedAction {
  return {
    type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_CONVERSATIONS_COMPLETED,
    payload: {
      panelId,
      conversationIds,
    },
  };
}

function fetchContactCardConversationsFailedAction(
  panelId: ContactCardPanelId,
): FetchContactCardConversationsFailedAction {
  return {
    type: ContactCardPanelActionsEnum.FETCH_CONTACT_CARD_CONVERSATIONS_FAILED,
    payload: {
      panelId,
    },
  };
}

/*
 * Union type.
 */

export type ContactCardAction =
  | FetchContactCardDataAction
  | FetchContactCardDataCompletedAction
  | FetchContactCardAccountAction
  | FetchContactCardAccountCompletedAction
  | SelectedCardUpdatedAction
  | FetchContactCardConversationsAction
  | FetchContactCardConversationsCompletedAction
  | FetchContactCardConversationsFailedAction;

export type ContactCardParentPanelId =
  | ViewerPanelId
  | MessagePanelId
  | EmlPanelId
  | CalendarsPanelId
  | AccountDetailsPanelId
  | ContactDetailsPanelId;

/*
 * Actions.
 */

export function navigateContactCardAction(
  panelId: ContactCardPanelId,
  parentPanelId: ContactCardParentPanelId,
  selectedContact: ContactModel,
  selectedCardId: number | undefined,
  location: ContactCardLocationsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const isUserVisitor = getIsUserVisitor(getState());
    const [cancelTokenId, cancelToken] = buildPanelCancelToken(panelId);

    dispatch(
      setPanelAction({
        id: panelId,
        parentPanelId,
        type: PanelTypesEnum.CONTACT_CARD,
        location,
        cancelTokenId,
        sequence: findPanelSequence(),
        selectedContactUid: selectedContact.uid,
        selectedCardId,
        status: computeContactCardStatus(isUserVisitor, selectedCardId),
      }),
    );

    // If the user is visitor, we are not able to fetch any cards/accounts.
    if (isUserVisitor) {
      return;
    }

    // If we have a pre-selected card id use it, otherwise search based on the handle.
    // This can happen async as we will update the redux store if we find a new card.
    if (!selectedCardId) {
      dispatch(searchForCardsFromHandleAsyncAction(panelId, selectedContact, cancelToken));
    } else {
      dispatch(fetchConversationsForContactCardAsyncAction(panelId, selectedContact));
    }

    // If we have a card, we may not have their account loaded, check to make sure it is also loaded.
    if (selectedCardId) {
      const cardAccountId = getCards(getState())[selectedCardId]?.accountId;
      if (cardAccountId) {
        dispatch(fetchContactCardAccountDataAsyncAction(panelId, cardAccountId));
      }
    }
  };
}

function computeContactCardStatus(isUserVisitor: boolean, selectedCardId?: number) {
  // If the user is a visitor, we can not do any fetching.
  if (isUserVisitor) {
    return AsyncStatusesEnum.LOADED;
  }
  // If we already have a card, we do not need to do any fetching.
  return selectedCardId ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.LOADING;
}

function searchForCardsFromHandleAsyncAction(
  panelId: ContactCardPanelId,
  selectedContact: ContactModel,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch) => {
    dispatch(fetchContactCardDataAction(panelId));

    const searchResult = await dispatch(searchForCardsAsyncAction(selectedContact.handle, {cancelToken}));

    // Make sure the cards from the search result match the contact uid we are looking for.
    const cards = _(searchResult?.cards ?? [])
      .filter((card) => card.contactUids.includes(selectedContact.uid))
      .value();
    // Build out the new contact based on the returned cards.
    const contacts = _(searchResult?.contacts ?? [])
      .filter((contact) => contact.uid === selectedContact.uid)
      .unionBy('uid')
      .map((contact) => ({
        ...contact,
        cardIds: _(cards).keyBy('namespace').mapValues('id').value(),
      }))
      .value();

    // Update the data in the store.
    dispatch(updateDataAction({cards, contacts}));

    // If we have more than 1 card, we need to retrieve based on priority order.
    const selectedCard = cards.length > 1 ? selectCardViaPriorityOrder(cards) : cards[0];

    // Check to make sure we have the account loaded.
    if (selectedCard?.accountId) {
      dispatch(fetchContactCardAccountDataAsyncAction(panelId, selectedCard.accountId));
    }

    batch(() => {
      dispatch(fetchContactCardDataCompletedAction(panelId, selectedCard?.id));
      dispatch(fetchConversationsForContactCardAsyncAction(panelId, selectedContact));
    });
  };
}

export function fetchContactCardAccountDataAsyncAction(
  panelId: ContactCardPanelId,
  accountId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = makeGetContactCardPanel()(getState(), {panelId});
    const cancelToken = findPanelCancelToken(panel.cancelTokenId);

    dispatch(fetchContactCardAccountAction(panelId));
    await dispatch(fetchOrGetAccountAsyncAction(accountId, {cancelToken}));
    dispatch(fetchContactCardAccountCompletedAction(panelId));
  };
}

export function navigateToContactDetailsFromContactCardAction(
  panelId: ContactCardPanelId,
  tab: ContactDetailsTabsEnum,
  card?: CardModel,
  account?: AccountModel,
  sectionToScrollTo?: ContactDetailsScrollSectionsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    if (tab === ContactDetailsTabsEnum.ACCOUNT && !account) {
      return;
    }

    const panel = makeGetContactCardPanel()(getState(), {panelId});
    const parentPanel = getPanels(getState())[panel.parentPanelId];

    if (!parentPanel) {
      return;
    }

    switch (parentPanel.type) {
      case PanelTypesEnum.MESSAGE:
      case PanelTypesEnum.VIEWER: {
        if (parentPanel.popupAppPanelId) {
          dispatch(
            openPopoutContactDetailsAction(
              card?.id,
              panel.selectedContactUid,
              parentPanel.id,
              tab,
              sectionToScrollTo,
            ),
          );
        } else {
          dispatch(
            openSidePanelContactDetailsAction(tab, panel.selectedContactUid, account?.id, sectionToScrollTo),
          );
        }
        break;
      }
      case PanelTypesEnum.EML:
      case PanelTypesEnum.ACCOUNT_DETAILS:
      case PanelTypesEnum.CONTACT_DETAILS:
      case PanelTypesEnum.CALENDARS:
      default:
        dispatch(
          openPopoutContactDetailsAction(
            card?.id,
            panel.selectedContactUid,
            parentPanel.id,
            tab,
            sectionToScrollTo,
          ),
        );
    }
  };
}

function openPopoutContactDetailsAction(
  selectedCardId: number | undefined,
  selectedContactUid: string,
  parentPanelId: NewPanelId,
  tab: ContactDetailsTabsEnum,
  sectionToScrollTo?: ContactDetailsScrollSectionsEnum,
): ThunkAction {
  return (dispatch) => {
    batch(() => {
      dispatch(
        popoutContactDetailsAction({
          parentPanelId,
          selectedContactUid,
          selectedCardId,
          sectionToScrollTo,
        }),
      );
      dispatch(updateContactDetailsPreferredTabAction(tab));
    });
  };
}

function openSidePanelContactDetailsAction(
  tab: ContactDetailsTabsEnum,
  selectedContactUid: string,
  accountId?: number,
  sectionToScrollTo?: ContactDetailsScrollSectionsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const panels = getFlatPanels(getState());
    const existingPanel = panels
      .filter(panelIsContactDetailsPanel)
      .find((panel) => panel.location === ContactDetailsPanelLocationsEnum.INBOXES);

    // For contacts: If we already have the existing panel, just update the selected contact.
    if (existingPanel && tab === ContactDetailsTabsEnum.CONTACT) {
      dispatch(updateContactDetailsPanelSelectedContactAction(existingPanel.id, selectedContactUid));
    }

    // For accounts: If we already have the existing panel, updated selected contact and open the account tab.
    if (existingPanel && tab === ContactDetailsTabsEnum.ACCOUNT) {
      dispatch(updateContactDetailsPanelSelectedContactAction(existingPanel.id, selectedContactUid));
      dispatch(contactDetailsOpenAccountDetailsAction(existingPanel.id, accountId));
    }

    // If we have an existing panel and want to scroll to a section set the scroll value.
    if (existingPanel && sectionToScrollTo) {
      dispatch(contactDetailsScrollToSectionAction(existingPanel.id, sectionToScrollTo));
    }

    if (!existingPanel) {
      batch(() => {
        dispatch(updateSelectedSideFrameIdAction(FrameTypesEnum.CUSTOMER_VIEW));
        dispatch(updateContactDetailsPreferredTabAction(tab));
      });
    }
  };
}

export function updateSelectedContactCardCardIdAction(
  panelId: ContactCardPanelId,
  cardId: number,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = makeGetContactCardPanel()(getState(), {panelId});

    // If the card is already selected, return early.
    if (panel.selectedCardId === cardId) {
      return;
    }

    // We may need to fetch account data for a card.
    const card = getCards(getState())[cardId];
    if (card?.accountId) {
      dispatch(fetchContactCardAccountDataAsyncAction(panelId, card.accountId));
    }

    dispatch(selectedCardUpdatedAction(panelId, cardId));
  };
}

export function contactCardAddCardToPrivateContactsAsyncAction(
  panelId: ContactCardPanelId,
  visualContact: VisualContactModel,
  card?: CardModel,
): ThunkAsyncAction {
  return async (dispatch) => {
    const cardId = await dispatch(createPrivateCardAsyncAction(card, visualContact));
    if (cardId) {
      dispatch(updateSelectedContactCardCardIdAction(panelId, cardId));
    }
  };
}

export function contactCardAddCardToCompanyContactsAsyncAction(
  panelId: ContactCardPanelId,
  visualContact: VisualContactModel,
  card?: CardModel,
): ThunkAsyncAction {
  return async (dispatch) => {
    const cardId = await dispatch(createCompanyCardAsyncAction(card, visualContact));
    if (cardId) {
      dispatch(updateSelectedContactCardCardIdAction(panelId, cardId));
    }
  };
}

export function contactCardComposeMessageForChannelTypeAction(
  channelType: ChannelTypesEnum,
  visualContact: VisualContactModel,
  card?: CardModel,
): ThunkAction {
  return (dispatch) => {
    const contactSource = getContactSourceFromMessageType(getMessageTypeFromChannelType(channelType));

    // If we have a card, we can look at the contact uids for the source we passed in.
    if (card) {
      const handlesForSource = computeHandlesFromContactUidsForSource(contactSource, card?.contactUids ?? []);

      // This should not happen but it is a fall-through if we fail to find a handle.
      if (handlesForSource.length === 0) {
        return;
      }

      // We can use the first handle since they are sorted alphabetically.
      const recipient = {
        contact: buildContactForHandle(contactSource, handlesForSource[0]),
        namespace: globalNamespace,
        role: RecipientRolesEnum.TO,
      };
      dispatch(
        composeMessageAction({
          recipients: [recipient],
          shouldForcePopout: true,
        }),
      );
      return;
    }

    // If we do not have a card we can just use the visual contact.
    const recipient = {
      contact: visualContact.contact,
      namespace: globalNamespace,
      role: RecipientRolesEnum.TO,
    };
    dispatch(
      composeMessageAction({
        recipients: [recipient],
        shouldForcePopout: true,
      }),
    );
  };
}

export function contactCardCreateNewCalendarEventAction(
  panelId: ContactCardPanelId,
  visualContact: VisualContactModel,
): ThunkAction {
  return (dispatch, getState) => {
    const {parentPanelId} = makeGetContactCardPanel()(getState(), {panelId});

    // If we are in the calendar, we can just add a new event.
    if (RCalendarsPanelId.is(parentPanelId)) {
      dispatch(createOrUpdateCalendarEventDraftAction(parentPanelId, [visualContact]));
      return;
    }

    // We need to pop out the calendar.
    const calendarPanelId = makeCalendarsPanelId(parentPanelId);

    // Check if we have an existing panel matching what we are trying to open.
    const existingPanel = getPanels(getState())[calendarPanelId];
    if (existingPanel) {
      batch(() => {
        dispatch(updateSelectedSideFrameIdAction(FrameTypesEnum.CALENDAR));
        dispatch(createOrUpdateCalendarEventDraftAction(calendarPanelId, [visualContact]));
      });
      return;
    }

    dispatch(
      navigateCalendarsAction(
        calendarPanelId,
        undefined,
        {
          shouldForcePopout: true,
          popupAppPanelId: makeGetAppPanelIdFromPanel()(getState(), {panelId: parentPanelId}),
        },
        [visualContact],
      ),
    );
  };
}

export function fetchConversationsForContactCardAsyncAction(
  panelId: ContactCardPanelId,
  selectedContact: ContactModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {selectedCardId} = makeGetContactCardPanel()(getState(), {panelId});
    const card = selectedCardId ? getCards(getState())[selectedCardId] : undefined;

    try {
      dispatch(fetchContactCardConversationsAction(panelId));

      // Fetch the conversations.
      const conversationIds = await dispatch(
        fetchContactCardConversationsAsyncAction(panelId, card, selectedContact),
      );

      dispatch(fetchContactCardConversationsCompletedAction(panelId, conversationIds));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchContactCardConversationsFailedAction(panelId));
    }
  };
}

function fetchContactCardConversationsAsyncAction(
  panelId: ContactCardPanelId,
  card?: CardModel | undefined,
  contact?: ContactModel | undefined,
): ThunkAsyncAction<ReadonlyArray<number>> {
  return async (dispatch, getState) => {
    const {cancelTokenId} = makeGetContactCardPanel()(getState(), {panelId});
    const cancelToken = findPanelCancelToken(cancelTokenId);

    if (!card && contact) {
      const contactConversations = await dispatch(
        fetchContactConversationsAsyncAction(contact, {cancelToken}, 21),
      );
      const [recentConversations] = splitConversationsIntoRecentAndOlder(
        contactConversations.conversations ?? [],
      );
      return recentConversations.map((conversation) => conversation.id);
    }

    if (!card) {
      return [];
    }

    const cardConversations = await dispatch(fetchCardConversationsAsyncAction(card, {cancelToken}, 21));
    const [recentConversations] = splitConversationsIntoRecentAndOlder(cardConversations.conversations ?? []);
    return recentConversations.map((conversation) => conversation.id);
  };
}

/*
 * Helpers.
 */

function computeHandlesFromContactUidsForSource(
  contactSource: ContactSourcesEnum,
  contactUids: ReadonlyArray<string>,
) {
  return _(contactUids)
    .map((contactUid) => parseContactUid(contactUid))
    .filter((parsed) => parsed[0] === contactSource)
    .map((parsed) => parsed[1])
    .sort((a, b) => compareName(a, b))
    .value();
}
